import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { isNativeMobile } from '../mobile/platform.js';

export type SafetyLocationState = {
  status: 'inactive' | 'starting' | 'sharing' | 'permission_required' | 'unavailable' | 'error';
  accuracy: number | null;
  reportedAt: string | null;
};

export function useMobileSafetyLocation(enabled: boolean): SafetyLocationState {
  const [state, setState] = useState<SafetyLocationState>({
    status: enabled ? 'starting' : 'inactive', accuracy: null, reportedAt: null,
  });
  const lastSentAt = useRef(0);

  useEffect(() => {
    if (!enabled || !isNativeMobile()) {
      setState({ status: 'inactive', accuracy: null, reportedAt: null });
      return;
    }
    if (!navigator.geolocation) {
      setState({ status: 'unavailable', accuracy: null, reportedAt: null });
      return;
    }
    let stopped = false;
    setState((current) => ({ ...current, status: 'starting' }));
    const watch = navigator.geolocation.watchPosition((position) => {
      if (stopped) return;
      const now = Date.now();
      if (now - lastSentAt.current < 60_000) return;
      lastSentAt.current = now;
      const reportedAt = new Date(position.timestamp).toISOString();
      void api.reportMobileLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        heading: position.coords.heading,
        speed: position.coords.speed,
        reportedAt,
      }).then(() => {
        if (!stopped) setState({ status: 'sharing', accuracy: position.coords.accuracy, reportedAt });
      }).catch(() => {
        if (!stopped) setState((current) => ({ ...current, status: 'error' }));
      });
    }, (error) => {
      if (stopped) return;
      setState((current) => ({
        ...current,
        status: error.code === error.PERMISSION_DENIED ? 'permission_required' : 'error',
      }));
    }, { enableHighAccuracy: true, maximumAge: 30_000, timeout: 20_000 });
    return () => { stopped = true; navigator.geolocation.clearWatch(watch); };
  }, [enabled]);

  return state;
}
